// Client-side mirror of the reuseMealPlan mutation's server-side day-shift
// algorithm (see .agents/integration-guides/2026_08_20-reuse_meal_plan/):
// a single day-offset is computed as target_start_date minus the earliest
// scheduled_date among the selected meals, then applied uniformly to every
// selected meal's date(s). Used to preview where meals will land before submit.

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { keyToUtcIso } from './dateHelpers'
import type { Meal } from '../graphql/types'

dayjs.extend(utc)

function shiftDateKey(dateStr: string, offsetDays: number): string {
  const key = dayjs.utc(dateStr).format('YYYY-MM-DD')
  const shiftedKey = dayjs.utc(key).add(offsetDays, 'day').format('YYYY-MM-DD')
  return keyToUtcIso(shiftedKey)
}

/**
 * Computes preview Meal objects showing where each selected meal will land
 * if reused into targetStartDate (a YYYY-MM-DD date key). Every field besides
 * the date(s) is preserved; `id` is prefixed so it never collides with a real
 * meal ID.
 */
export function computeReusePreviewMeals(selectedMeals: Meal[], targetStartDate: string): Meal[] {
  if (selectedMeals.length === 0 || !targetStartDate) return []

  const earliestDate = selectedMeals.reduce(
    (earliest, meal) => (dayjs.utc(meal.scheduled_date).isBefore(dayjs.utc(earliest)) ? meal.scheduled_date : earliest),
    selectedMeals[0].scheduled_date,
  )

  // Normalize to the earliest meal's calendar day (strip its time-of-day) before
  // diffing — otherwise a meal with a time later than midnight UTC (e.g. a 2:30pm
  // dinner) truncates the day count short by one, landing the preview a day early.
  const offsetDays = dayjs.utc(targetStartDate).diff(dayjs.utc(earliestDate).startOf('day'), 'day')

  return selectedMeals.map((meal) => ({
    ...meal,
    id: `preview-${meal.id}`,
    scheduled_date: shiftDateKey(meal.scheduled_date, offsetDays),
    scheduled_dates: meal.scheduled_dates?.length ? meal.scheduled_dates.map((d) => shiftDateKey(d, offsetDays)) : undefined,
  }))
}
